/**
 * Operating areas: the boundary a route is planned against.
 *
 * Keep-in and keep-out are drawn in different colours because they mean
 * opposite things, and a planner glancing at a shaded polygon has to know
 * whether the aircraft belongs inside it or outside it without reading a
 * legend.
 *
 * The zones are shared through /api/zones. Drawn here they are enforced on the
 * ground only; whether a given aircraft also holds them as its own fence is in
 * the Planning fence panel, because the map cannot show the difference.
 */

import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { subscribeToMissionGeometry, type Zone } from '@/stores/missionStore';
import { useEffect } from 'react';

export const ZONE_SOURCE_ID = 'operating-zones';
const ZONE_FILL_LAYER = 'operating-zones-fill';
const ZONE_LINE_LAYER = 'operating-zones-line';
const ZONE_KEEP_IN_LAYER = 'operating-zones-keep-in';
const ZONE_LABEL_LAYER = 'operating-zones-label';

/**
 * Segments used to draw a circular zone.
 *
 * 64 keeps the chord error under 0.2 percent of the radius, so a 500 m circle
 * is never more than a metre off. Fewer segments and a keep-out ring visibly
 * cut its own corners, which on a 50 m exclusion is the whole margin.
 */
const CIRCLE_SEGMENTS = 64;

const EARTH_RADIUS_M = 6_371_000;

/** Project a point along a bearing. Mirrors api/app/domain/geo.move_point. */
export function movePoint(
  lat: number,
  lon: number,
  bearingDeg: number,
  distanceM: number,
): [number, number] {
  const toRad = Math.PI / 180;
  const phi1 = lat * toRad;
  const lambda1 = lon * toRad;
  const theta = bearingDeg * toRad;
  const delta = distanceM / EARTH_RADIUS_M;

  const phi2 = Math.asin(
    Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta),
  );
  const lambda2 =
    lambda1 +
    Math.atan2(
      Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
      Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2),
    );

  const lon2 = ((lambda2 / toRad + 540) % 360) - 180;
  return [lon2, phi2 / toRad];
}

/** A circle as a closed ring of [lon, lat] pairs, geodesically correct. */
export function circleRing(
  lat: number,
  lon: number,
  radiusM: number,
  segments = CIRCLE_SEGMENTS,
): Array<[number, number]> {
  const ring: Array<[number, number]> = [];
  for (let i = 0; i < segments; i += 1) {
    ring.push(movePoint(lat, lon, (i * 360) / segments, radiusM));
  }
  const first = ring[0];
  if (first) ring.push(first);
  return ring;
}

export function zoneFeature(zone: Zone): unknown {
  const ring =
    zone.shape.type === 'circle'
      ? circleRing(zone.shape.lat, zone.shape.lon, zone.shape.radiusM)
      : closeRing(zone.shape.points.map((point) => [point.lon, point.lat] as [number, number]));

  return {
    type: 'Feature',
    properties: {
      zoneId: zone.zoneId,
      name: zone.name,
      kind: zone.kind,
      color: zone.kind === 'keep-out' ? '#f0563f' : '#4ec9a3',
    },
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

/** GeoJSON polygons must repeat their first vertex; a UI never collects it. */
function closeRing(points: Array<[number, number]>): Array<[number, number]> {
  if (points.length < 3) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return points;
  if (first[0] === last[0] && first[1] === last[1]) return points;
  return [...points, first];
}

export interface ZoneLayer {
  destroy: () => void;
  stats: () => { redraws: number; zones: number };
}

export function createZoneLayer(map: MapLike): ZoneLayer {
  let redraws = 0;
  let zoneCount = 0;

  if (!map.getSource(ZONE_SOURCE_ID)) {
    map.addSource(ZONE_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(ZONE_FILL_LAYER)) {
    map.addLayer({
      id: ZONE_FILL_LAYER,
      type: 'fill',
      source: ZONE_SOURCE_ID,
      paint: {
        'fill-color': ['get', 'color'],
        // Low enough that imagery stays readable underneath. A planner picks
        // waypoints off the ground texture, and a 0.3 fill hid the hedge line
        // an approach was being planned around.
        'fill-opacity': 0.12,
      },
    });
  }

  // Two line layers rather than one with an expression: `line-dasharray` is not
  // data-driven in MapLibre, so a per-feature dash has to be a per-layer dash
  // behind a filter.
  if (!map.getLayer(ZONE_LINE_LAYER)) {
    map.addLayer({
      id: ZONE_LINE_LAYER,
      type: 'line',
      source: ZONE_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'keep-out'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
    });
  }

  if (!map.getLayer(ZONE_KEEP_IN_LAYER)) {
    map.addLayer({
      id: ZONE_KEEP_IN_LAYER,
      type: 'line',
      source: ZONE_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'keep-in'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-dasharray': [3, 2] },
    });
  }

  if (!map.getLayer(ZONE_LABEL_LAYER)) {
    map.addLayer({
      id: ZONE_LABEL_LAYER,
      type: 'symbol',
      source: ZONE_SOURCE_ID,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'symbol-placement': 'line',
        'text-offset': [0, -0.8],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const unsubscribe = subscribeToMissionGeometry(({ zones }) => {
    const source = geoJsonSource(map, ZONE_SOURCE_ID);
    if (!source) return;
    source.setData({ type: 'FeatureCollection', features: zones.map(zoneFeature) });
    zoneCount = zones.length;
    redraws += 1;
  });

  return {
    destroy: () => {
      unsubscribe();
      for (const id of [ZONE_LABEL_LAYER, ZONE_KEEP_IN_LAYER, ZONE_LINE_LAYER, ZONE_FILL_LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(ZONE_SOURCE_ID)) map.removeSource(ZONE_SOURCE_ID);
    },
    stats: () => ({ redraws, zones: zoneCount }),
  };
}

export function useZoneLayer(): void {
  const controller = useMapController();
  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createZoneLayer(map);
        return () => layer.destroy();
      }),
    [controller],
  );
}
